<template>
  <ul
    class="key-mapper"
    :role="role"
    data-test="graphics-keyMapper"
  >
    <li v-if="depth === 0 && !data.length">
      <template name="empty">
        <p class="text-center padding-md font-weight-bold">No results found</p>
      </template>
    </li>
    <li
      v-else
      v-for="({ key, label, children }, idx) in data"
      :key="`${key}-index`"
      class="key-mapper__node">
      <div
        class="key-mapper__node-content"
        :class="{
          'key-mapper__node-content--selected': checkSelected(key),
          'key-mapper__node-content--multiple': !children && multiple,
        }"
        role="treeitem">
        <span
          class="fas fa-fw key-mapper__toggle-icon"
          :class="{
            'fa-chevron-right': children && !expanded[idx],
            'fa-chevron-down': children && expanded[idx],
          }"></span>
        <template v-if="children">
          <slot
            name="collapse"
            :label="label"
            :on="{
              click: () => toggleCollapse(idx),
            }"
            data-test="graphics-keyMapper-collapseLabel"
          >
            <button
              class="key-mapper__collapse-btn"
              data-test="graphics-keyMapper-collapseBtn"
              data-entity="graphics-keyMapper-collapseBtn"
              @click="toggleCollapse(idx)"
            >
              {{ label }}
            </button>
          </slot>
        </template>

        <template v-else-if="multiple">
          <slot name="multiple" :path="key" :label="label">
            <label
              class="checkbox key-mapper__checkbox"
              :class="{
                'key-mapper__checkbox--disabled':
                  localSelected.length === limit &&
                  !localSelected.includes(key),
              }">
              <input
                v-model="localSelected"
                type="checkbox"
                :value="key"
                :disabled="
                  localSelected.length === limit && !localSelected.includes(key)
                "
                :data-test="`graphics-keyMapperCheckbox-${key}`"
                :data-entity="`graphics-keyMapperCheckbox-${key}`"
              />
              {{ label }}
              <div class="checkmark">
                <span class="checkmark-tick fa-solid fa-check"></span>
              </div>
            </label>
          </slot>
        </template>

        <template v-else>
          <slot
            name="single"
            :path="key"
            :label="label"
            :on="{
              click: () => (localSelected = key),
            }">
            <button
              class="key-mapper__single-select"
              data-test="graphics-keyMapperSingleSelect"
              data-entity="graphics-keyMapperSingleSelect"
              @click="localSelected = key"
            >
              {{ label }}
            </button>
          </slot>
        </template>
      </div>

      <template v-if="children">
        <key-mapper
          v-show="expanded[idx]"
          class="key-mapper__collapse-content"
          v-model="localSelected"
          :data="children"
          :multiple="multiple"
          :limit="limit"
          :force-expand="forceExpand"
          :depth="depth + 1"
          role="group">
          <template v-for="(_, slot) of $slots" v-slot:[slot]="scope">
            <slot :name="slot" v-bind="scope" />
          </template>
        </key-mapper>
      </template>
    </li>
  </ul>
</template>

<script>
export default {
  emits: ['update:modelValue'],
  name: 'KeyMapper',
  props: {
    data: {
      required: true,
    },
    modelValue: {
      required: true,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    // limit number that can be multiple selected
    limit: {
      required: false,
      default: false,
    },
    // used to force expand all collapsed items (used for searching)
    forceExpand: {
      required: false,
      type: Boolean,
      default: false,
    },
    role: {
      default: 'tree',
    },
    depth: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      expanded: [],
    };
  },
  computed: {
    localSelected: {
      get() {
        return this.modelValue;
      },
      set(v) {
        this.$emit('update:modelValue', v);
      },
    },
    shouldLimit() {
      return this.multiple && this.localSelected.length === this.limit;
    },
  },
  watch: {
    forceExpand() {
      this.checkExpanded();
    },
    data() {
      this.checkExpanded();
    },
  },
  created() {
    this.checkExpanded();
  },
  methods: {
    checkExpanded() {
      this.expanded = this.data.map((d) => this.forceExpand || this.checkSelected(d.key));
    },
    checkSelected(key) {
      const checkMatch = (str) => str.startsWith(key);

      if (Array.isArray(this.localSelected)) {
        return this.localSelected.some(checkMatch);
      }

      return checkMatch(this.localSelected);
    },
    toggleCollapse(idx) {
      const currentValue = this.expanded[idx] || false;
      this.expanded[idx] = !currentValue;
    },
  },
};
</script>
